import React, { useEffect, useState, useRef } from "react";
import { AddCircleOutline, Delete, Edit } from "@mui/icons-material";
import { Button, IconButton, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CompanyVeiwController from "../../redux/actions/companyApiActions";
import CompanyFormModal from "./AddCompany";
import "../../styles/styles.css";
import "../../styles/layout.css";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import CompanyViewModal from "./CompanyViewModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExportButton from "../../components/ag grid/ExportButton";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ProtectedImage from "../../components/ProtectedImage";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CompanyMobileView from "./CompanyMobileView";
import ConfirmDialog from "../../components/ConfirmDialog";

const CompanyList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { getCompanies, deleteCompany } = CompanyVeiwController();
  const { companies } = useSelector((state) => state.company);

  useEffect(() => {
    dispatch(getCompanies());
  }, []);

  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const executeDelete = () => {
    if (confirmDeleteId) {
      dispatch(deleteCompany(confirmDeleteId));
      setConfirmDeleteId(null);
    }
  };
  const handleEdit = (company) => {
    setEditCompany(company);
    setShowModal(true);
  };
  const handleView = (company) => {
    setSelectedCompany(company);
    setViewModalOpen(true);
  };
  const columnDefs = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 52,
      maxWidth: 52,
      minWidth: 52,
      pinned: "left",
      suppressSizeToFit: true,
      sortable: false,
      headerClass: "checkbox-selection-header",
      cellClass: "checkbox-selection-cell",
    },
    {
      headerName: "SR.No",
      valueGetter: (params) => params.node.rowIndex + 1,
      width: 72,
      maxWidth: 80,
      minWidth: 72,
      suppressSizeToFit: true,
    },
    {
      headerName: "Company Name",
      field: "company_name",
      flex: 2,
      minWidth: 160,
      cellRenderer: (params) => {
        const maxLength = 40;
        const value = params.value || "";
        return value.length > maxLength
          ? value.substring(0, maxLength) + "..."
          : value;
      },
    },
    {
      headerName: "Logo",
      field: "company_logo",
      width: 90,
      minWidth: 90,
      maxWidth: 100,
      suppressSizeToFit: true,
      cellRenderer: (params) =>
        params.value ? (
          <ProtectedImage
            path={params.value}
            alt="Company Logo"
            className="company-logo"
          />
        ) : (
          "N/A"
        ),
    },
    {
      headerName: "Description",
      field: "description",
      flex: 2,
      minWidth: 160,
      cellRenderer: (params) => {
        const maxLength = 40;
        const value = params.value || "";
        return value.length > maxLength
          ? value.substring(0, maxLength) + "..."
          : value;
      },
    },
    {
      headerName: "Location",
      field: "location",
      flex: 1,
      minWidth: 120,
    },
    {
      headerName: "Emp Strength",
      field: "employee_strength",
      width: 120,
      minWidth: 110,
      maxWidth: 140,
      suppressSizeToFit: true,
    },
    {
      headerName: "Actions",
      field: "actions",
      pinned: "right",
      width: 130,
      minWidth: 130,
      maxWidth: 130,
      suppressSizeToFit: true,
      sortable: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
          <Tooltip title="View">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleView(params.data)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.data)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{ color: "#d32f2f" }}
              onClick={() => handleDelete(params.data.id)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  const filteredCompanies = React.useMemo(() => {
    if (!searchTerm.trim()) return companies;
    const q = searchTerm.toLowerCase();
    return companies.filter(
      (c) =>
        c.company_name?.toLowerCase().includes(q) ||
        c.location?.toLowerCase().includes(q)
    );
  }, [companies, searchTerm]);

  return (
    <>
      {isMobile ? (
        <CompanyMobileView
          companies={filteredCompanies}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleView={handleView}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          openModalForCreate={() => {
            setEditCompany(null);
            setShowModal(true);
          }}
          canCreate={true}
          canEdit={true}
          canDelete={true}
        />
      ) : (
        <ListPageLayout
      title="Companies"
      subtitle="Manage registered organizations"
      headerAction={
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutline />}
          onClick={() => {
            setEditCompany(null);
            setShowModal(true);
          }}
          sx={toolbarButtonSx}
        >
          Add Company
        </Button>
      }
      toolbarRight={
        <>
          <ExportButton
            gridRef={gridRef}
            fileName="CompanyList.xlsx"
            exportColumns={[
              "company_name",
              "description",
              "location",
              "employee_strength",
              "email",
            ]}
            disabled={selectedRows.length === 0}
          />
          <PageSizeSelector
            pageSize={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          />
        </>
      }
    >
      <div className="list-page-grid">
        <ReusableGrid
          rowData={companies}
          columnDefs={columnDefs}
          gridRef={gridRef}
          pagination={true}
          pageSize={pageSize}
          onSelectionChanged={(rows) => setSelectedRows(rows)}
          emptyMessage="No companies yet"
          emptyHint='Click "Add Company" to create your first organization.'
        />
      </div>

      </ListPageLayout>
      )}

      <CompanyFormModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        initialData={editCompany}
      />
      <CompanyViewModal
        show={viewModalOpen}
        handleClose={() => setViewModalOpen(false)}
        company={selectedCompany}
      />
      <ConfirmDialog
        open={Boolean(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={executeDelete}
        title="Delete Company"
        content="Are you sure you want to delete this company? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
      />
    </>
  );
};

export default CompanyList;
